import IconComputer from "@common/components/icons/IconComputer";
import IconGlobe from "@common/components/icons/IconGlobe";
import IconUsers from "@common/components/icons/IconUsers";
import TextWithIcon from "@common/components/thirdParty/mantine/text/TextWithIcon";
import HealthStatus from "@common/components/ui/health/HealthStatus";
import { Flex } from "@mantine/core";
import { STATUS_CHANGE_REGION } from "@stores/instances/constants";
import {
  formatInstanceVersion,
  getInstanceStatus,
} from "@stores/instances/utils";
import { isCanceledOrDeleted } from "@stores/purchases/utils";
import { ReactElement } from "react";
import { IPurchaseStatusProps } from "./types";
import { pluralize } from "@common/utils/string";
import { useReloadInstanceWhenChanging } from "@hooks/useReloadInstanceWhenChanging";

export default function PurchaseStatus({
  purchase: thePurchase,
}: IPurchaseStatusProps): ReactElement | null {
  const { purchase: purchaseWhenChanging } =
    useReloadInstanceWhenChanging(thePurchase);

  const purchase = purchaseWhenChanging || thePurchase;
  const { hosted_instance, user_count } = purchase;
  const { status, mb_version, region } = hosted_instance || {};

  const isPurchaseCanceledOrDeleted = isCanceledOrDeleted(purchase);
  const instanceHealth = hosted_instance && getInstanceStatus(hosted_instance);

  const hasHealthStatus = !isPurchaseCanceledOrDeleted && instanceHealth;
  const hasMBVersion = !!mb_version;
  const hasRegion =
    !isPurchaseCanceledOrDeleted && status !== STATUS_CHANGE_REGION && region;
  const hasUsersCount =
    !isPurchaseCanceledOrDeleted &&
    typeof user_count === "number" &&
    user_count > 0;

  return hasHealthStatus || hasMBVersion || hasRegion || hasUsersCount ? (
    <Flex
      align={{ base: "flex-start", xs: "center" }}
      direction={{ base: "column", xs: "row" }}
      gap={{ base: "xs", xs: "md" }}
      mt="xs"
    >
      {hasHealthStatus && (
        <HealthStatus health={instanceHealth} displayHealth mb="0" mt="0" />
      )}
      {hasMBVersion && (
        <TextWithIcon
          icon={<IconComputer />}
          text={formatInstanceVersion(mb_version)}
          tooltip="Instance's Metabase version"
        />
      )}
      {hasRegion && (
        <TextWithIcon
          icon={<IconGlobe size={12} />}
          text={region}
          tooltip="Instance's Metabase region"
        />
      )}
      {hasUsersCount && (
        <TextWithIcon
          icon={<IconUsers />}
          text={`${user_count} ${pluralize("users", user_count)}`}
        />
      )}
    </Flex>
  ) : null;
}
